import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { scoreBadgeClass, scoreBarClass } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import Navbar from '@/components/Navbar';
import {
  Users, Trophy, Target, TrendingUp, Building2, BarChart3,
  Download, ChevronRight, Clock, User, FileText
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ManagerDashboard() {
  const navigate = useNavigate();
  const { axiosAuth, token } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axiosAuth().get('/manager/dashboard')
      .then(res => setData(res.data))
      .catch(() => toast.error('Failed to load dashboard'))
      .finally(() => setLoading(false));
  }, [axiosAuth]);

  const exportCSV = (type) => {
    fetch(`${API}/export/csv/${type}`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.blob())
      .then(blob => { const a = document.createElement('a'); a.href = URL.createObjectURL(blob); a.download = `${type}_export.csv`; a.click(); })
      .catch(() => toast.error('Export failed'));
  };

  const downloadPDF = () => {
    fetch(`${API}/manager/summary-pdf`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => { if (!r.ok) throw new Error(); return r.blob(); })
      .then(blob => { const a = document.createElement('a'); a.href = URL.createObjectURL(blob); a.download = 'manager_summary.pdf'; a.click(); })
      .catch(() => toast.error('Failed to generate PDF'));
  };

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="heritage-card p-8 text-center"><div className="w-8 h-8 border-2 border-brand border-t-transparent rounded-full animate-spin mx-auto mb-4" /><p className="font-body text-sm">Loading...</p></div>
    </div>
  );

  if (!data) return null;

  const stats = [
    { label: 'Members', value: data.total_members, icon: Users, color: 'text-brand' },
    { label: 'Simulations', value: data.total_simulations, icon: Target, color: 'text-info' },
    { label: 'Completed', value: data.total_completed, icon: Trophy, color: 'text-success' },
    { label: 'Team Avg', value: data.team_avg_score ? `${data.team_avg_score}/10` : '--', icon: TrendingUp, color: 'text-warning' },
  ];

  return (
    <div className="min-h-screen bg-background" data-testid="manager-dashboard-page">
      <Navbar showBack backTo="/" backLabel="Manager Dashboard" />

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-6 sm:py-10">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div>
            <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight">Manager Dashboard</h1>
            <div className="flex items-center gap-2 mt-1">
              {data.team_name && <Badge className="bg-success/10 text-success border-none text-xs">{data.team_name}</Badge>}
              {data.org_name && <Badge className="bg-brand/10 text-brand border-none text-xs">{data.org_name}</Badge>}
            </div>
          </div>
          <div className="flex gap-2">
            <Button data-testid="export-team-pdf" onClick={downloadPDF}
              className="h-8 px-3 bg-brand text-[#333333] gap-1.5 text-xs rounded-lg hover:opacity-90 font-medium shadow-none">
              <FileText className="w-3.5 h-3.5" /> Summary PDF
            </Button>
            <Button data-testid="export-team-csv" onClick={() => exportCSV('team-report')}
              className="h-8 px-3 bg-transparent border border-border text-foreground hover:bg-muted gap-1.5 text-xs rounded-lg shadow-none">
              <Download className="w-3.5 h-3.5" /> Export CSV
            </Button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
          {stats.map((s, i) => (
            <div key={i} className="heritage-card p-4" data-testid={`mgr-stat-${i}`}>
              <div className="flex items-center gap-2 mb-1"><s.icon className={`w-4 h-4 ${s.color}`} /><span className="text-[11px] font-medium text-muted-foreground uppercase tracking-wide">{s.label}</span></div>
              <p className="font-heading text-xl sm:text-2xl font-bold">{s.value}</p>
            </div>
          ))}
        </div>

        {/* Team Members Table */}
        <div className="heritage-card p-5 mb-6" data-testid="team-members-section">
          <h2 className="font-heading text-sm font-bold mb-4 flex items-center gap-2"><Users className="w-4 h-4 text-muted-foreground" /> Team Members ({data.members.length})</h2>
          {data.members.length === 0 ? (
            <p className="text-xs text-muted-foreground text-center py-6">No team members found. Assign yourself to a team in Organization Management.</p>
          ) : (
            <div className="space-y-1.5">
              {data.members.map((m, i) => (
                <div key={m.id} className="flex items-center gap-3 p-3 rounded-xl hover:bg-muted/50 transition-colors" data-testid={`member-${i}`}>
                  <div className="w-9 h-9 bg-brand text-[#333333] rounded-full flex items-center justify-center font-heading font-bold text-xs flex-shrink-0">
                    {m.name?.charAt(0)?.toUpperCase() || 'U'}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-heading text-xs font-bold truncate">{m.name}</p>
                    <p className="font-body text-[10px] text-muted-foreground">{m.email}</p>
                  </div>
                  <div className="flex items-center gap-4 flex-shrink-0">
                    <div className="text-center hidden sm:block">
                      <p className="font-heading text-xs font-bold">{m.completed}</p>
                      <p className="font-body text-[9px] text-muted-foreground">Completed</p>
                    </div>
                    <div className="text-center">
                      <p className={`font-heading text-sm font-bold ${m.avg_score >= 7 ? 'text-success' : m.avg_score >= 5 ? 'text-warning' : m.avg_score > 0 ? 'text-accent' : 'text-muted-foreground'}`}>{m.avg_score || '--'}</p>
                      <p className="font-body text-[9px] text-muted-foreground">Avg</p>
                    </div>
                    <div className="w-16 h-1.5 bg-muted rounded-full overflow-hidden hidden sm:block">
                      <div className={`h-full rounded-full ${scoreBarClass(m.avg_score)}`} style={{ width: `${(m.avg_score / 10) * 100}%` }} />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Recent Completions */}
        {data.recent_completions?.length > 0 && (
          <div className="heritage-card p-5" data-testid="recent-completions">
            <h2 className="font-heading text-sm font-bold mb-4 flex items-center gap-2"><Clock className="w-4 h-4 text-muted-foreground" /> Recent Completions</h2>
            <div className="space-y-1.5">
              {data.recent_completions.map((r, i) => (
                <div key={i} className="flex items-center gap-3 p-3 rounded-xl hover:bg-muted/50 transition-colors cursor-pointer" data-testid={`recent-${i}`}
                  onClick={() => navigate(`/report/${r.id}`)}>
                  <div className="w-8 h-8 bg-success/10 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Trophy className="w-4 h-4 text-success" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-heading text-xs font-bold truncate">{r.scenario_title}</p>
                    <div className="flex items-center gap-2">
                      <span className="font-body text-[10px] text-muted-foreground flex items-center gap-1"><User className="w-2.5 h-2.5" />{r.user_name}</span>
                      <Badge className="bg-muted text-muted-foreground border-none text-[9px] px-1 py-0">{r.difficulty}</Badge>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <Badge className={`${scoreBadgeClass(r.score?.overall_score || 0)} border-none text-xs px-2 py-0.5`}>
                      {r.score?.overall_score || 0}/10
                    </Badge>
                    <ChevronRight className="w-3.5 h-3.5 text-muted-foreground" />
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
